import { createApi } from '@reduxjs/toolkit/query/react'

import { baseQueryWithReAuth } from './base-query'
import type {
	CreateInvoiceBody,
	CreateTransactionBody,
	FinanceMetrics,
	FinanceMetricsParams,
	FinanceSeriesParams,
	FinanceSeriesResponse,
	FinanceTransaction,
	Invoice,
	ListFinanceTransactionsParams,
	ListFinanceTransactionsResponse,
	ListInvoicesParams,
	ListInvoicesResponse,
} from './types/finance'

export const financesApi = createApi({
	reducerPath: 'financesApi',
	baseQuery: baseQueryWithReAuth,
	tagTypes: ['FinanceMetrics', 'FinanceInvoice', 'FinanceTransaction', 'RentalInvoice'],
	endpoints: (builder) => ({
		getFinanceMetrics: builder.query<FinanceMetrics | null, FinanceMetricsParams | void>({
			query: (params) => ({ url: '/finances/metrics', params: params ?? {} }),
			providesTags: ['FinanceMetrics'],
		}),

		getFinanceSeries: builder.query<FinanceSeriesResponse, FinanceSeriesParams>({
			query: (params) => ({ url: '/finances/series', params }),
			providesTags: ['FinanceMetrics'],
		}),

		listFinanceInvoices: builder.query<ListInvoicesResponse, ListInvoicesParams | void>({
			query: (params) => ({ url: '/finances/invoices', params: params ?? {} }),
			providesTags: ['FinanceInvoice'],
		}),

		createFinanceInvoice: builder.mutation<Invoice | null, CreateInvoiceBody>({
			query: (body) => ({ url: '/finances/invoices', method: 'POST', body }),
			invalidatesTags: (result) => [
				'FinanceInvoice',
				'FinanceMetrics',
				// Invalidate rental invoice cache so newly created invoice appears in rental card
				{ type: 'RentalInvoice', id: result?.rentalId ?? 'LIST' },
			],
		}),

		listFinanceTransactions: builder.query<
			ListFinanceTransactionsResponse,
			ListFinanceTransactionsParams | void
		>({
			query: (params) => ({ url: '/finances/transactions', params: params ?? {} }),
			providesTags: ['FinanceTransaction'],
		}),

		createFinanceTransaction: builder.mutation<FinanceTransaction | null, CreateTransactionBody>({
			query: (body) => ({ url: '/finances/transactions', method: 'POST', body }),
			invalidatesTags: ['FinanceTransaction', 'FinanceInvoice', 'FinanceMetrics'],
		}),
	}),
})

export const {
	useGetFinanceMetricsQuery,
	useGetFinanceSeriesQuery,
	useListFinanceInvoicesQuery,
	useCreateFinanceInvoiceMutation,
	useListFinanceTransactionsQuery,
	useCreateFinanceTransactionMutation,
} = financesApi
